import React, { useEffect } from "react";
import AOS from "aos";
import SEO from "../components/SEO";
import { motion } from "framer-motion";
import Hero from "../components/Hero";

const AdmissionsPolicyPage = () => {
    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    const heroSlides = [
        {
            id: 1,
            image: "/assets/img/prog-1.jpg",
            title: "Admissions Policy",
            subtitle:
                "Explore our admission pathways for diploma, undergraduate, and postgraduate programs.",
            buttonText: "Apply Now",
            buttonLink: "/apply",
        },
    ];

    return (
        <section className="bg-gray-100 min-h-screen pt-12">
            <SEO 
                title="Admissions — Africa Research University" 
                description="Admissions policy, requirements and application guidance for ARU."
                url="/admissions"
                breadcrumbs={[
                    { name: 'Home', url: 'https://africaresearchuniversity.com' },
                    { name: 'About', url: 'https://africaresearchuniversity.com/about' },
                    { name: 'Admissions', url: 'https://africaresearchuniversity.com/admissions' }
                ]}
            />

            <div className="container mx-auto px-6 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border p-6 sm:p-10"
                >
                    <h2
                        className="text-3xl font-bold text-center text-green-700 mb-10"
                        data-aos="fade-up"
                    >
                        Admissions Policy
                    </h2>

                    {/* Admissions Table */}
                    <div className="overflow-x-auto" data-aos="fade-up">
                        <table className="min-w-full border border-gray-200 rounded-lg text-sm sm:text-base">
                            <thead className="bg-green-600 text-white">
                                <tr>
                                    <th className="py-3 px-4 text-left">Category</th>
                                    <th className="py-3 px-4 text-left">Degree</th>
                                    <th className="py-3 px-4 text-left">Admission Requirements</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {/* A - School Leavers' Entry */}
                                <tr>
                                    <td className="py-3 px-4 font-semibold text-gray-800">
                                        A - School Leavers' Entry
                                    </td>
                                    <td className="py-3 px-4">Diploma</td>
                                    <td className="py-3 px-4">
                                        1. Grade Twelve (12) School Certificate with five credits or
                                        better <br /> 2. ‘O’ level equivalent
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="py-3 px-4">Undergraduate</td>
                                    <td className="py-3 px-4">
                                        1. Grade Twelve (12) School Certificate with five credits or
                                        better <br /> 2. ‘O’ level equivalent
                                    </td>
                                </tr>

                                {/* B - Professional Entry */}
                                <tr>
                                    <td className="py-3 px-4 font-semibold text-gray-800">
                                        B - Professional Entry
                                    </td>
                                    <td className="py-3 px-4">Diploma</td>
                                    <td className="py-3 px-4">
                                        1. Grade Twelve (12) School Certificate with five credits or
                                        better <br /> 2. ‘O’ level equivalent <br /> 3. Work
                                        experience is considered
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="py-3 px-4">Undergraduate</td>
                                    <td className="py-3 px-4">
                                        1. Grade Twelve (12) School Certificate with five credits or
                                        better <br /> 2. ‘O’ level equivalent <br /> 3. Diploma in a
                                        related field <br /> 4. More than 2 years' work experience
                                        (optional)
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="py-3 px-4">Graduate and Post Graduate</td>
                                    <td className="py-3 px-4">
                                        1. Bachelor's Degree <br /> 2. At least two (2) years' work
                                        experience
                                    </td>
                                </tr>

                                {/* C - Mature Entry */}
                                <tr>
                                    <td className="py-3 px-4 font-semibold text-gray-800">
                                        C - Mature Entry (45 Years and Older)
                                    </td>
                                    <td className="py-3 px-4">Diploma</td>
                                    <td className="py-3 px-4">
                                        1. Grade Twelve (12) School Certificate with work experience
                                        or equivalent <br /> 2. Work experience is considered
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="py-3 px-4">Undergraduate</td>
                                    <td className="py-3 px-4">
                                        1. Grade Twelve (12) School Certificate with supporting work
                                        experience <br /> 2. Diploma holders
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Note Section */}
                    <div
                        className="mt-8 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded"
                        data-aos="fade-up"
                    >
                        <p className="text-gray-800 italic">
                            * For international students, please ensure that you have your
                            certificates validated and evaluated by Zambia Qualifications
                            Authority (ZAQA) prior to submitting your Application Form to The
                            University.
                        </p>
                    </div>

                    {/* Download Button */}
                    <div className="text-center mt-10" data-aos="fade-up">
                        <a
                            href="https://irp-cdn.multiscreensite.com/13badcb3/files/uploaded/20210317%20-%20Rules%20%26%20Regulations%20-%20Revised.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-green-600 text-white font-semibold px-6 py-3 rounded-full shadow-md hover:bg-green-700 transition duration-300"
                        >
                            Download Rules and Regulations
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default AdmissionsPolicyPage;
